import { reportCmsContentIssue } from "@portal/app/_common/lib/reportCmsContentIssue";
import {
  RENDERABLE_FORM_QUESTION_TYPE,
  isRenderableFormQuestionType,
} from "@portal/app/_common/types/formFunnel";
import {
  isDynamicConcentrationQuestion,
  isFormFunnelZipQuestion,
} from "@portal/app/_common/lib/formFunnelDynamicOptions";
import type {
  PayloadFormQuestion,
  PayloadQuestionConfig,
  PopulatedPayloadFormFunnel,
} from "@portal/app/_server/services/formFunnel/payloadTypes";
import type { QuestionConfig } from "../FormFieldRenderer";

/** A configured screen, before answer-dependent visibility is evaluated. */
export type FunnelStep =
  | { type: "question"; questions: QuestionConfig[] }
  | { type: "interstitial"; source: PayloadFormQuestion };

function mapQuestionToConfig(q: PayloadFormQuestion): QuestionConfig | null {
  if (!q.type) return null;
  if (!isRenderableFormQuestionType(q.type)) {
    reportCmsContentIssue({
      source: "buildFunnelSteps",
      collection: "form-questions",
      issue: "unsupported_question_type",
      value: q.type,
      identifier: q.fieldKey,
      message:
        `[buildFunnelSteps] Unsupported question type "${q.type}" for field "${q.fieldKey}". ` +
        `Supported types: ${RENDERABLE_FORM_QUESTION_TYPE.join(", ")}`,
    });
    return null;
  }

  return {
    id: q.fieldKey,
    label: q.label,
    type: q.type,
    options: q.options || [],
    required: q.validation?.required ?? false,
    conditionalLogic: q.conditionalLogic as QuestionConfig["conditionalLogic"],
    filterMapping: q.filterMapping,
    customerFieldMapping: q.customerFieldMapping,
    autocomplete: q.autocomplete,
    inputMode: q.inputMode,
    pageGroup: q.pageGroup,
    validation: {
      pattern: q.validation?.pattern,
      customError: q.validation?.customError,
      minLength: q.validation?.minLength,
      maxLength: q.validation?.maxLength,
    },
    description: q.description?.content
      ? { content: q.description.content, placement: q.description.placement }
      : undefined,
  };
}

/** Shared assembly for the customer journey and the CMS screen catalog. */
export function buildFunnelSteps(
  funnel: Pick<PopulatedPayloadFormFunnel, "questions" | "questionConfig">
): FunnelStep[] {
  const overrideMap = new Map<string, PayloadQuestionConfig>();
  for (const qc of funnel.questionConfig ?? []) {
    if (qc.fieldKey) overrideMap.set(qc.fieldKey, qc);
  }

  const steps: FunnelStep[] = [];
  const pageGroupIndexMap = new Map<string, number>();
  // ZIP ordering is validated at author time. Preserve the configured order.
  const orderedQuestions = funnel.questions ?? [];
  const hasDynamicConcentrationQuestion = orderedQuestions.some(isDynamicConcentrationQuestion);

  for (const q of orderedQuestions) {
    if (q.isInterstitial) {
      steps.push({ type: "interstitial", source: q });
      continue;
    }
    const config = mapQuestionToConfig(q);
    if (!config) continue;

    const override = overrideMap.get(q.fieldKey);
    if (override?.pageGroup !== undefined) {
      config.pageGroup = override.pageGroup || undefined;
    }

    const shouldIsolateZipStep = hasDynamicConcentrationQuestion && isFormFunnelZipQuestion(config);
    const pageGroup = shouldIsolateZipStep ? undefined : config.pageGroup;

    if (pageGroup) {
      const existingIndex = pageGroupIndexMap.get(pageGroup);
      if (existingIndex !== undefined) {
        const existingStep = steps[existingIndex];
        if (existingStep.type === "question") existingStep.questions.push(config);
        continue;
      }
      pageGroupIndexMap.set(pageGroup, steps.length);
    }
    steps.push({ type: "question", questions: [config] });
    if (shouldIsolateZipStep) pageGroupIndexMap.clear();
  }
  return steps;
}

/** Retain a preview frame when its screen moves or its copy changes. */
export function getFunnelStepKey(step: FunnelStep): string {
  return step.type === "interstitial"
    ? `interstitial:${step.source.id}`
    : `question:${JSON.stringify(step.questions.map((question) => question.id).sort())}`;
}
